import { TableFilter } from "@neonumy/ant-design";
import { useGetWindowHeight } from "@neonumy/neonumy-ui";
import { Typography } from "antd";
import { DATE_FORMAT_API } from "config/settings";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import useNotification from "utils/hooks/useNotification";
import useDebitsTableColumns from "views/Payments/hooks/useDebitsTableColumns";
import useDirectDebitApi from "views/Payments/hooks/useDirectDebitApi";
import CancelDebits from "./CancelDebits";
import CreateUpdateDebits from "./CreateUpdateDebits";
import ValidateDebits from "./ValidateDebits";

export default function Debits(): React.ReactElement {
  const windowHeight = useGetWindowHeight();
  const urlParams: RouteParams = useParams();
  const { notificationError } = useNotification();
  const { t } = useTranslation();

  const [id, setId] = useState<ID>("");
  const [showValidateModal, setShowValidateModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [filters, setFilters] = useState<any>({
    groups_CompanyId_In: [urlParams.companyId],
    date_Lte: undefined,
    date_Gte: undefined,
    status: undefined,
  });

  const paginationInit = {
    offset: 0,
    limit: 10,
  };

  const {
    listDirectDebits,
    directDebitsData: dataSource,
    refetchListDirectDebits: refetch,
    loadinglistDirectDebits: loading,
    deleteDirectDebit,
    totalCount,
    listDirectDebitStatus,
    directDebitsOptions,
  } = useDirectDebitApi();

  const onFinish = async (actionType: string, id: ID) => {
    setId(id);

    if (actionType === "validate") {
      setShowValidateModal(true);
      return;
    }

    if (actionType === "cancel") {
      setShowCancelModal(true);
      return;
    }

    if (actionType === "delete") {
      const response = await deleteDirectDebit({
        variables: {
          id,
        },
      });

      const errors = response?.data?.deleteDirectDebit?.errors;
      notificationError(errors);
      refetch();
    }
  };

  const COLUMNS = useDebitsTableColumns(onFinish, directDebitsOptions);

  useEffect(() => {
    if (urlParams.companyId) {
      listDirectDebits({
        variables: {
          ...paginationInit,
          filter: { groups_CompanyId_In: [urlParams.companyId] },
        },
      });

      listDirectDebitStatus();
    }
  }, []);

  const handleResetFilter = (resetedFilter: Record<string, unknown>, confirm?: any) => {
    confirm();
    setCurrentPage(1);
    setFilters((filters: Record<string, unknown>) => ({
      ...filters,
      ...resetedFilter,
    }));
    refetch({
      ...filters,
      ...resetedFilter,
    });
  };

  const handlePagination = (page: number, size: number) => {
    setPageSize(size);
    setCurrentPage(page);
    refetch({
      filter: { groups_CompanyId_In: [urlParams.companyId] },
      limit: size,
      offset: size * (page - 1),
    });
  };

  const handleSearchFilters = async (confirm: () => void) => {
    confirm();
    setCurrentPage(1);

    if (totalCount !== undefined) {
      const refetchFilters = filters;

      if (refetchFilters.date_Lte) {
        refetchFilters.date_Lte = dayjs(refetchFilters.date_Lte).format(DATE_FORMAT_API);
      }

      if (refetchFilters.date_Gte) {
        refetchFilters.date_Gte = dayjs(refetchFilters.date_Gte).format(DATE_FORMAT_API);
      }

      refetch({
        ...paginationInit,
        filter: { ...filters },
        limit: pageSize,
      });
    }
  };

  return (
    <>
      <Typography.Title className="documents-title" level={4}>
        {t("Debits")}
      </Typography.Title>
      <TableFilter
        className="antd-newrai-table"
        columns={COLUMNS}
        scroll={{ y: windowHeight - 350 }}
        dataSource={dataSource}
        loading={loading}
        pagination={{
          onChange: handlePagination,
          showSizeChanger: true,
          current: currentPage,
          pageSize: pageSize,
          total: totalCount,
          showTotal: total => t("Total table items", { count: total }),
        }}
        defaultSearchFilter={handleSearchFilters}
        defaultResetFilter={handleResetFilter}
        defaultFiltersObject={filters}
        defaultFilterSet={setFilters}
        debounced={true}
      />
      <CreateUpdateDebits refetch={refetch} />
      <ValidateDebits
        refetch={refetch}
        id={id}
        isModalVisible={showValidateModal}
        setIsModalVisible={setShowValidateModal}
      />
      <CancelDebits
        refetch={refetch}
        id={id}
        isModalVisible={showCancelModal}
        setIsModalVisible={setShowCancelModal}
      />
    </>
  );
}
